import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Loader2, Plus, Trash2, Pencil, ExternalLink } from "lucide-react";

const EMPTY = {
  title: "", slug: "", excerpt: "", cover_image: "", body: "",
  meta_title: "", meta_description: "", author: "Rokert Retreats",
  tags: "", read_minutes: 4, published: true,
};

export default function AdminBlog() {
  const [posts, setPosts] = useState(null);
  const [editing, setEditing] = useState(null); // null = none, {} = form open

  const load = () => api.get("/admin/blog").then((r) => setPosts(r.data)).catch(() => setPosts([]));
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ ...EMPTY });
  const startEdit = (p) => setEditing({ ...p, tags: (p.tags || []).join(", ") });

  const save = async () => {
    const payload = {
      ...editing,
      read_minutes: parseInt(editing.read_minutes) || 4,
      tags: (editing.tags || "").split(",").map((t) => t.trim()).filter(Boolean),
    };
    try {
      if (editing.post_id) await api.put(`/admin/blog/${editing.post_id}`, payload);
      else await api.post("/admin/blog", payload);
      toast.success("Article saved");
      setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try { await api.delete(`/admin/blog/${p.post_id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  if (posts === null) return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "#B08D57" }} /></div>;

  if (editing) {
    return (
      <div data-testid="admin-blog-editor" className="max-w-3xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-2xl">{editing.post_id ? "Edit article" : "New article"}</h2>
          <button onClick={() => setEditing(null)} className="text-sm" style={{ color: "#6B7280" }}>Cancel</button>
        </div>
        <div className="space-y-4 rounded-2xl border bg-white p-6" style={{ borderColor: "#E5E7EB" }}>
          <Field label="Title" v={editing.title} on={(x) => setEditing({ ...editing, title: x })} testid="blog-title" />
          <Field label="Slug (optional — auto from title)" v={editing.slug} on={(x) => setEditing({ ...editing, slug: x })} testid="blog-slug" />
          <Field label="Cover image URL" v={editing.cover_image} on={(x) => setEditing({ ...editing, cover_image: x })} testid="blog-cover" />
          <Field label="Excerpt" v={editing.excerpt} on={(x) => setEditing({ ...editing, excerpt: x })} area testid="blog-excerpt" />
          <Field label="Body (Markdown)" v={editing.body} on={(x) => setEditing({ ...editing, body: x })} area rows={14} testid="blog-body" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Author" v={editing.author} on={(x) => setEditing({ ...editing, author: x })} testid="blog-author" />
            <Field label="Read minutes" type="number" v={editing.read_minutes} on={(x) => setEditing({ ...editing, read_minutes: x })} testid="blog-read" />
          </div>
          <Field label="Tags (comma separated)" v={editing.tags} on={(x) => setEditing({ ...editing, tags: x })} testid="blog-tags" />
          <div className="pt-2 border-t" style={{ borderColor: "#E5E7EB" }}>
            <p className="text-xs uppercase tracking-wider mb-2" style={{ color: "#B08D57" }}>SEO</p>
            <Field label="Meta title" v={editing.meta_title} on={(x) => setEditing({ ...editing, meta_title: x })} testid="blog-metatitle" />
            <div className="mt-3"><Field label="Meta description" v={editing.meta_description} on={(x) => setEditing({ ...editing, meta_description: x })} area testid="blog-metadesc" /></div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} data-testid="blog-published" />
            Published (visible on the site)
          </label>
          <button onClick={save} data-testid="blog-save"
            className="w-full py-2.5 rounded-xl text-white flex items-center justify-center gap-2" style={{ background: "#111111" }}>
            Save Article
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-blog">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading text-2xl">Journal</h2>
        <button onClick={startNew} data-testid="blog-new"
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm" style={{ background: "#B08D57" }}>
          <Plus className="w-4 h-4" /> New article
        </button>
      </div>
      {posts.length === 0 && <p style={{ color: "#6B7280" }}>No articles yet.</p>}
      <div className="space-y-3">
        {posts.map((p) => (
          <div key={p.post_id} data-testid={`admin-blog-row-${p.slug}`} className="flex items-center gap-4 rounded-xl border bg-white p-4" style={{ borderColor: "#E5E7EB" }}>
            <img src={p.cover_image} alt="" className="w-20 h-14 object-cover rounded-lg shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-heading text-lg truncate">{p.title}</h3>
                {!p.published && <span className="text-[10px] uppercase px-2 py-0.5 rounded-full" style={{ background: "#FEE2E2", color: "#B91C1C" }}>Draft</span>}
              </div>
              <p className="text-xs truncate" style={{ color: "#6B7280" }}>/blog/{p.slug} · {p.read_minutes} min</p>
            </div>
            <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="p-2 rounded-lg" style={{ color: "#6B7280" }} title="View"><ExternalLink className="w-4 h-4" /></a>
            <button onClick={() => startEdit(p)} data-testid={`blog-edit-${p.slug}`} className="p-2 rounded-lg" style={{ color: "#111827" }}><Pencil className="w-4 h-4" /></button>
            <button onClick={() => del(p)} data-testid={`blog-delete-${p.slug}`} className="p-2 rounded-lg" style={{ color: "#B91C1C" }}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({ label, v, on, type = "text", area = false, rows = 3, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider" style={{ color: "#6B7280" }}>{label}</label>
      {area ? (
        <textarea value={v} onChange={(e) => on(e.target.value)} rows={rows} data-testid={testid}
          className="w-full border rounded-lg px-3 py-2.5 mt-1 text-sm font-mono" style={{ borderColor: "#E5E7EB" }} />
      ) : (
        <input type={type} value={v} onChange={(e) => on(e.target.value)} data-testid={testid}
          className="w-full border rounded-lg px-3 py-2.5 mt-1 text-sm" style={{ borderColor: "#E5E7EB" }} />
      )}
    </div>
  );
}
